import { useEffect, useState } from 'react';
import { Box, Button, Dialog, DialogActions } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Shift } from '../../resourceTracking/types';
import { hasPermission } from '../../../auth';
import { useBreakDisplayQuery } from '../queryHooks';
import { useBreakDisplayUserSettings } from '../userSettings';

interface BreakTimeOverlayProps {
  mCellId: string;
  shift: Shift;
}

/**
 * Shows the break-time announcement in a dialog that covers most of the screen while the selected
 * cell + shift is on a scheduled break; renders nothing otherwise.
 *
 * - Dismiss (backdrop, Esc, or button) records this break's identity in a per-user setting, so a
 *   reload during the same break doesn't re-show it — a later break still triggers.
 * - Don't Show Again (Leads+) sets a per-user setting that suppresses break displays permanently.
 * - The dialog self-closes at breakEndsAt as a fail-safe, so it can never get stuck.
 */
const BreakTimeOverlay = ({ mCellId, shift }: BreakTimeOverlayProps) => {
  const { t } = useTranslation();
  const { data } = useBreakDisplayQuery(mCellId, shift);
  const [visible, setVisible] = useState(false);
  const {
    dismissedBreak,
    disabled: breakDisplayDisabled,
    dismissBreak,
    disableBreakDisplay,
  } = useBreakDisplayUserSettings();
  const canNeverShowAgain = hasPermission(['BreakDisplay.NeverShowAgain']);

  const breakEndsAt = data?.isOnBreak ? data.breakEndsAt : null;
  const url = data?.url ?? null;
  const breakKey = breakEndsAt ? `${mCellId}|${shift}|${breakEndsAt}` : null;

  useEffect(() => {
    if (
      breakDisplayDisabled ||
      !breakEndsAt ||
      !url ||
      breakKey === dismissedBreak
    ) {
      setVisible(false);
      return;
    }

    const msUntilEnd = new Date(breakEndsAt).getTime() - Date.now();
    if (msUntilEnd <= 0) {
      setVisible(false);
      return;
    }

    setVisible(true);
    const timer = setTimeout(() => setVisible(false), msUntilEnd);
    return () => clearTimeout(timer);
  }, [breakEndsAt, url, breakKey, dismissedBreak, breakDisplayDisabled]);

  if (!visible || !url) {
    return null;
  }

  const dismiss = () => {
    if (breakKey) {
      dismissBreak(breakKey);
    }
  };

  return (
    <Dialog
      open
      onClose={dismiss}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: '90vw',
          height: '90vh',
          m: 0,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <Box
          component='iframe'
          src={url}
          title='Break time notification'
          sandbox='allow-scripts allow-same-origin allow-forms allow-popups'
          allowFullScreen
          sx={{
            border: 'none',
            width: '100%',
            height: '100%',
            display: 'block',
          }}
        />
      </Box>
      <DialogActions sx={{ px: 3, py: 2 }}>
        {canNeverShowAgain && (
          <Button color='inherit' onClick={disableBreakDisplay}>
            {t('dontShowAgain')}
          </Button>
        )}
        <Button variant='contained' onClick={dismiss}>
          {t('dismiss')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BreakTimeOverlay;
